/**
 * Which rows of a table are going into the next run.
 *
 * One hook for the three lists an operator calls from - the scanner, the
 * scanner's leads and the business register - rather than a copy of the same
 * `Set` juggling in each. They differ in every column they draw and agree on
 * exactly this: a row is a company with a number on it, and a morning's work is
 * a handful of them picked out of a filter.
 *
 * A `Map` and not a `Set` because the target has to survive paging. The
 * selection outlives the page it was made on, and the row that carried the
 * company name is by then no longer loaded - keeping only the key would mean
 * calling a number with no idea whose it is.
 *
 * Returns null for anyone who cannot place calls. That is the whole permission
 * check: a table asks for picks, gets null, and draws no checkbox column at
 * all, rather than offering a selection whose one action is refused.
 */
import { useCallback, useMemo, useState } from "react";
import type { CallTarget } from "../callState";
import { useCallContext } from "../CallProvider";

export interface QueuePicks {
  size: number;
  has: (key: string) => boolean;
  /** Pick or unpick one row. The target is what will be dialled. */
  toggle: (key: string, target: CallTarget) => void;
  /** The head checkbox: every callable row on this page at once. */
  setMany: (entries: ReadonlyArray<{ key: string; target: CallTarget }>, on: boolean) => void;
  clear: () => void;
  /**
   * The run, in the order the rows were picked.
   *
   * Insertion order rather than the table's current sort, and they are the same
   * thing in the case that matters: the head checkbox inserts a page in the
   * order it is drawn, so "select all, call them" walks the list the operator
   * sorted it into.
   */
  targets: CallTarget[];
}

export function useQueuePicks(): QueuePicks | null {
  const ctx = useCallContext();
  const [picked, setPicked] = useState<Map<string, CallTarget>>(() => new Map());

  const toggle = useCallback((key: string, target: CallTarget) => {
    setPicked((prev) => {
      const next = new Map(prev);
      if (next.has(key)) next.delete(key);
      else next.set(key, target);
      return next;
    });
  }, []);

  const setMany = useCallback(
    (entries: ReadonlyArray<{ key: string; target: CallTarget }>, on: boolean) => {
      setPicked((prev) => {
        const next = new Map(prev);
        for (const entry of entries) {
          if (on) next.set(entry.key, entry.target);
          else next.delete(entry.key);
        }
        return next;
      });
    },
    [],
  );

  const clear = useCallback(() => setPicked(new Map()), []);
  const has = useCallback((key: string) => picked.has(key), [picked]);
  const targets = useMemo(() => [...picked.values()], [picked]);

  const enabled = !!ctx?.enabled;
  return useMemo(
    () => (enabled ? { size: picked.size, has, toggle, setMany, clear, targets } : null),
    [enabled, picked.size, has, toggle, setMany, clear, targets],
  );
}
